import { useState } from "react";

const phases = [
  {
    id: "P1",
    title: "Video Engine & Navigation",
    color: "#E8FF47",
    accent: "#1a1a1a",
    icon: "▶",
    queue: "Ship First",
    summary: "Instant playback, smart preloading, fluid inter-episode UX",
    items: [
      {
        id: "P1-1",
        title: "Episode Premiere Testing & Alerts",
        priority: "high",
        description: "Lock content until air time, auto-unlock, and fan out push notifications.",
        shipCriteria: [
          "Countdown renders on episode card for any scheduled episode",
          "Episode auto-unlocks at scheduled_at within ±5 seconds",
          "Push notification delivered to test device within 60s of unlock",
          "Tested with 3 different future premiere times in staging",
        ],
        techStack: ["Expo Notifications", "AWS EventBridge", "Node.js Lambda"],
        tasks: [
          "Create `premiere_schedule` table: (id, episode_id, scheduled_at, status, notified_at)",
          "Build AWS EventBridge rule per premiere — triggers Lambda at scheduled time",
          "Lambda: set episode status = published, fan out Expo push notifications to subscribers",
          "Add <PremiereCountdown> component shown on episode card when status === 'scheduled'",
          "Add push permission gate during onboarding if not yet granted",
          "Test: schedule premiere 5 min out in staging, verify full flow end-to-end",
        ],
        code: `// premiereService.ts
export async function scheduleEpisodePremiere(episodeId: string, premiereAt: Date) {
  await db.premieres.create({ episodeId, scheduledAt: premiereAt, status: 'scheduled' });

  await eventBridge.putRule({
    Name: \`premiere-\${episodeId}\`,
    ScheduleExpression: \`cron(\${toCron(premiereAt)})\`,
    State: 'ENABLED',
  });

  await eventBridge.putTargets({
    Rule: \`premiere-\${episodeId}\`,
    Targets: [{ Id: '1', Arn: PREMIERE_LAMBDA_ARN, Input: JSON.stringify({ episodeId }) }],
  });
}

// Lambda handler
export async function handler({ episodeId }) {
  await db.episodes.update({ id: episodeId }, { status: 'published' });
  const subscribers = await db.subscriptions.findWatchers(episodeId);
  await expoPush.sendAll(subscribers.map(s => ({
    to: s.pushToken,
    title: 'Now Streaming',
    body: 'A new episode just premiered — watch now!',
    data: { episodeId },
  })));
}`,
      },
      {
        id: "P1-2",
        title: "Inter-Episode Navigation",
        priority: "high",
        description: "Move between episodes without leaving the player — next/prev + season switcher.",
        shipCriteria: [
          "Tapping Next loads next episode without returning to browse screen",
          "Watch position is saved before switching episodes",
          "Season pill switcher reloads episode list for selected season",
          "First/last episode edge cases handled — no crash, correct UI state",
        ],
        techStack: ["React Native", "Expo Video", "Zustand"],
        tasks: [
          "Add currentEpisodeIndex, episodeList, savePosition() to player Zustand store",
          "Render bottom nav tray in player: Prev thumb | episode title + progress | Next thumb",
          "Animate tray in on tap, auto-hide after 3s inactivity with fade-out",
          "Season switcher: horizontal pill scroll above tray, re-fetches episode list on tap",
          "Hide Prev on episode 1; show 'Season Finale' badge instead of Next on last episode",
          "Call savePosition() before index change — persist to watch_history",
        ],
        code: `// usePlayerNavigation.ts
export function usePlayerNavigation() {
  const { episodeList, currentIndex, setCurrentIndex, savePosition } = usePlayerStore();

  const goToNext = useCallback(async () => {
    if (currentIndex < episodeList.length - 1) {
      await savePosition();
      setCurrentIndex(currentIndex + 1);
    }
  }, [currentIndex, episodeList]);

  const goToPrev = useCallback(async () => {
    if (currentIndex > 0) {
      await savePosition();
      setCurrentIndex(currentIndex - 1);
    }
  }, [currentIndex]);

  return {
    goToNext,
    goToPrev,
    hasNext: currentIndex < episodeList.length - 1,
    hasPrev: currentIndex > 0,
    isFinale: currentIndex === episodeList.length - 1,
  };
}`,
      },
      {
        id: "P1-3",
        title: "Preload Next Episode First Segment",
        priority: "high",
        description: "At 80% through an episode, silently cache the next episode's first HLS segment.",
        shipCriteria: [
          "No extra network activity until playback crosses 80% threshold",
          "Only preloads on WiFi — verified with NetInfo check",
          "Player picks up cached segment on episode transition (no re-download logged)",
          "Cache entries purged after 30 minutes",
        ],
        techStack: ["expo-file-system", "expo-av", "NetInfo"],
        tasks: [
          "Track progress via onPlaybackStatusUpdate — calculate positionMillis / durationMillis",
          "At 80%, resolve next episode HLS manifest URL from Zustand store",
          "Fetch manifest, parse first .ts segment URL from the playlist",
          "Check WiFi via NetInfo.fetch() before downloading — bail on mobile data",
          "Download segment to FileSystem.cacheDirectory with downloadAsync()",
          "Player checks segmentCache.get(episodeId) before network request on load",
          "On app foreground: purge cache entries older than 30 min",
        ],
        code: `// useNextEpisodePreload.ts
export function useNextEpisodePreload(episodeId: string, progressPct: number) {
  const nextEpisode = useNextEpisode(episodeId);
  const preloaded = useRef(false);

  useEffect(() => {
    if (progressPct >= 0.8 && !preloaded.current && nextEpisode) {
      preloaded.current = true;
      NetInfo.fetch().then(({ type }) => {
        if (type === 'wifi') {
          preloadFirstSegment(nextEpisode.hlsUrl).then(localUri => {
            segmentCache.set(nextEpisode.id, { uri: localUri, cachedAt: Date.now() });
          });
        }
      });
    }
  }, [progressPct, nextEpisode]);
}

async function preloadFirstSegment(hlsUrl: string): Promise<string> {
  const manifest = await fetch(hlsUrl).then(r => r.text());
  const firstSegUrl = resolveFirstSegment(manifest, hlsUrl);
  const dest = FileSystem.cacheDirectory + \`seg_\${Date.now()}.ts\`;
  await FileSystem.downloadAsync(firstSegUrl, dest);
  return dest;
}`,
      },
      {
        id: "P1-4",
        title: "Detect & Preload on Browse Page",
        priority: "medium",
        description: "Preload the top visible item's first segment so tapping play feels instant.",
        shipCriteria: [
          "Preload triggers only when item is 60%+ in viewport for 400ms+ (not on fast scroll)",
          "Only fires on WiFi",
          "Play on a preloaded item starts within 300ms on test device",
          "No duplicate preload calls for the same episode",
        ],
        techStack: ["FlashList", "NetInfo", "expo-file-system"],
        tasks: [
          "Configure viewabilityConfig: { itemVisiblePercentThreshold: 60, minimumViewTime: 400 }",
          "In onViewableItemsChanged, grab the top viewable item",
          "Check WiFi before preloading — bail if on mobile data",
          "Guard with a preloadedIds Set to prevent duplicate fetches per session",
          "Show subtle animated dot on play button while preloading is active",
          "On Play tap: player checks segmentCache first — use local URI if available",
        ],
        code: `// BrowsePreloader.tsx
const preloadedIds = new Set<string>();
const viewabilityConfig = { itemVisiblePercentThreshold: 60, minimumViewTime: 400 };

function BrowsePreloader({ data }) {
  const onViewableItemsChanged = useCallback(({ viewableItems }) => {
    const top = viewableItems[0]?.item;
    if (!top?.hlsUrl || preloadedIds.has(top.id)) return;

    NetInfo.fetch().then(({ type }) => {
      if (type !== 'wifi') return;
      preloadedIds.add(top.id);
      preloadFirstSegment(top.hlsUrl).then(uri => {
        segmentCache.set(top.id, { uri, cachedAt: Date.now() });
      });
    });
  }, []);

  return (
    <FlashList
      data={data}
      onViewableItemsChanged={onViewableItemsChanged}
      viewabilityConfig={viewabilityConfig}
      renderItem={({ item }) => <EpisodeCard item={item} />}
      estimatedItemSize={220}
    />
  );
}`,
      },
      {
        id: "P1-5",
        title: "Swipe to Skip / Rewind",
        priority: "medium",
        description: "Horizontal swipe on the player scrubs the timeline — distance maps to time delta.",
        shipCriteria: [
          "Swipe right skips forward, left rewinds — no conflict with vertical scroll",
          "Time delta overlay fades in during swipe, fades out 600ms after gesture ends",
          "Haptic fires once on gesture release",
          "Seek commits on gesture end only — no mid-pan seeking stutters",
        ],
        techStack: ["react-native-gesture-handler", "Reanimated 3", "expo-haptics"],
        tasks: [
          "Wrap player in GestureDetector with a horizontal-only Pan gesture",
          "Configure activeOffsetX: [-10, 10] and failOffsetY: [-10, 10] to avoid scroll conflicts",
          "Map translationX to time delta: 50px = 10s, clamp to ±60s",
          "Drive animated overlay with useSharedValue — show '<< Xs' or 'Xs >>' label",
          "On .onEnd(): call runOnJS(commitSeek)(delta), fade overlay out after 600ms delay",
          "Trigger Haptics.impactAsync(ImpactFeedbackStyle.Light) on seek commit",
        ],
        code: `// SwipeToSeek.tsx
export function SwipeToSeek({ playerRef, children }) {
  const seekDelta = useSharedValue(0);
  const overlayVisible = useSharedValue(0);

  const commitSeek = useCallback((delta: number) => {
    playerRef.current?.setPositionAsync(currentPosition + delta * 1000);
    Haptics.impactAsync(ImpactFeedbackStyle.Light);
  }, [currentPosition]);

  const swipeGesture = Gesture.Pan()
    .activeOffsetX([-10, 10])
    .failOffsetY([-10, 10])
    .onUpdate(e => {
      seekDelta.value = clamp(Math.round((e.translationX / 50) * 10), -60, 60);
      overlayVisible.value = 1;
    })
    .onEnd(() => {
      runOnJS(commitSeek)(seekDelta.value);
      seekDelta.value = 0;
      overlayVisible.value = withDelay(600, withTiming(0));
    });

  return (
    <GestureDetector gesture={swipeGesture}>
      <View style={StyleSheet.absoluteFill}>
        {children}
        <SeekOverlay delta={seekDelta} visible={overlayVisible} />
      </View>
    </GestureDetector>
  );
}`,
      },
    ],
  },
  {
    id: "P2",
    title: "Payments & Subscriptions",
    color: "#47FFB2",
    accent: "#001a0d",
    icon: "◈",
    queue: "Ship Second",
    summary: "Stripe + Mobile Money + in-app purchases, unified entitlement layer",
    items: [
      {
        id: "P2-1",
        title: "Subscribe Flow — In-App Purchases",
        priority: "critical",
        description: "Native iOS/Android IAP for subscriptions. Stripe web checkout as fallback.",
        shipCriteria: [
          "Products load from App Store / Play Store on SubscribeSheet open",
          "Successful purchase updates user entitlement within 5s (server-side verification)",
          "Premium content unlocks immediately — no app restart required",
          "Expired subscription correctly revokes access on next app open",
        ],
        techStack: ["expo-in-app-purchases", "Stripe", "Node.js"],
        tasks: [
          "Define SKUs in App Store Connect + Google Play: binyuma_basic_monthly, binyuma_premium_monthly, binyuma_premium_annual",
          "Install expo-in-app-purchases — fetch and cache products on app start",
          "Build <SubscribeSheet>: plan cards with feature bullets, price, and CTA button",
          "Purchase flow: initiate → receive transactionReceipt → POST to /subscriptions/verify",
          "Backend verifies receipt with Apple/Google, updates user_subscriptions table",
          "Build useSubscription() hook used across app to gate content rendering",
          "Web: backend creates Stripe Checkout Session, frontend opens URL in browser",
          "Handle restore purchases for users reinstalling the app",
        ],
        code: `// useSubscription.ts
export function useSubscription() {
  const { data, refetch } = useQuery(['subscription'], fetchMySubscription);

  const subscribe = async (sku: string) => {
    await InAppPurchases.connectAsync();
    const result = await InAppPurchases.purchaseItemAsync(sku);

    if (result.responseCode === IAPResponseCode.OK) {
      await api.post('/subscriptions/verify', {
        receipt: result.results[0].transactionReceipt,
        platform: Platform.OS,
        sku,
      });
      await refetch();
    }
  };

  return {
    isPremium: data?.plan === 'premium' && new Date(data.expiresAt) > new Date(),
    isBasic: data?.plan === 'basic',
    isFree: !data || data.plan === 'free',
    subscribe,
    restore: () => InAppPurchases.getPurchaseHistoryAsync(),
  };
}`,
      },
      {
        id: "P2-2",
        title: "Stripe — Cards & Web Payments",
        priority: "critical",
        description: "Card payments for subscriptions and per-episode/season purchases.",
        shipCriteria: [
          "Card entry via PaymentSheet works on iOS and Android",
          "Webhook updates entitlements on invoice.paid within 10s",
          "Subscription cancellation removes access at period end (not immediately)",
          "Test card 4242 succeeds; 4000 0000 0000 9995 shows decline error gracefully",
        ],
        techStack: ["@stripe/stripe-react-native", "Stripe Webhooks", "Node.js"],
        tasks: [
          "Create Stripe account — confirm UGX / USD currency support and Uganda business country",
          "Backend: POST /payments/create-intent (one-time) and POST /payments/create-subscription",
          "Install @stripe/stripe-react-native, wrap app root in <StripeProvider publishableKey={...}>",
          "Build payment UI using usePaymentSheet() — handles card input natively per platform",
          "Set up Stripe webhook endpoint — verify signature with stripe.webhooks.constructEvent()",
          "Handle: invoice.paid → extend subscription, customer.subscription.deleted → expire access",
          "Send receipt email on payment_intent.succeeded via AWS SES or SendGrid",
          "Store Stripe customer_id on user record for returning customers",
        ],
        code: `// server: payments.route.ts
router.post('/create-intent', authenticate, async (req, res) => {
  const { amount, currency = 'ugx', episodeId } = req.body;
  const customer = await getOrCreateStripeCustomer(req.user);

  const intent = await stripe.paymentIntents.create({
    amount,
    currency,
    customer: customer.id,
    metadata: { userId: req.user.id, episodeId: episodeId ?? '' },
    automatic_payment_methods: { enabled: true },
  });
  res.json({ clientSecret: intent.client_secret });
});

// Webhook handler
router.post('/webhook', express.raw({ type: 'application/json' }), (req, res) => {
  const event = stripe.webhooks.constructEvent(
    req.body, req.headers['stripe-signature'], WEBHOOK_SECRET
  );
  if (event.type === 'invoice.paid') {
    const { userId } = event.data.object.metadata;
    extendSubscription(userId, event.data.object.lines.data[0].period.end);
  }
  res.sendStatus(200);
});`,
      },
      {
        id: "P2-3",
        title: "Mobile Money — MTN & Airtel",
        priority: "critical",
        description: "MTN MoMo + Airtel Money via Flutterwave — essential for Uganda/East Africa.",
        shipCriteria: [
          "Phone number auto-detects MTN vs Airtel by Ugandan prefix",
          "USSD push prompt appears on user's phone within 10s of initiating",
          "Entitlement updated within 2 min of user approving on USSD",
          "Timeout (3 min) and insufficient funds both show clear retry UI",
        ],
        techStack: ["Flutterwave API", "Node.js", "React Native"],
        tasks: [
          "Register on Flutterwave — covers MTN MoMo, Airtel Money, and more in one API",
          "Backend: POST /payments/mobile-money — initiates collection, returns flw_ref",
          "Set up Flutterwave webhook receiver for async payment status updates",
          "Build <MobileMoneySheet>: phone input + network badge (auto-detect by UG prefix)",
          "MTN prefixes: 077, 078, 076, 039 | Airtel: 070, 075, 074",
          "Poll payment status every 15s as fallback if webhook not received within 30s",
          "Show animated 'Waiting for USSD confirmation...' state with 3-min countdown",
          "On success: update entitlements same as Stripe flow; on timeout: allow retry",
        ],
        code: `// mobileMoneyService.ts
const MTN_PREFIXES = ['077', '078', '076', '039'];
const AIRTEL_PREFIXES = ['070', '075', '074'];

export function detectNetwork(phone: string): 'MTN' | 'AIRTEL' | null {
  const prefix = phone.replace(/^(\+?256|0)/, '0').slice(0, 3);
  if (MTN_PREFIXES.includes(prefix)) return 'MTN';
  if (AIRTEL_PREFIXES.includes(prefix)) return 'AIRTEL';
  return null;
}

export async function initiateMoMoPayment(params: {
  phone: string; network: 'MTN' | 'AIRTEL'; amount: number; userId: string;
}) {
  const res = await flutterwaveAPI.post('/charges?type=mobile_money_uganda', {
    tx_ref: \`binyuma-\${params.userId}-\${Date.now()}\`,
    amount: params.amount,
    currency: 'UGX',
    phone_number: params.phone,
    network: params.network,
    email: await getUserEmail(params.userId),
    redirect_url: \`\${API_URL}/payments/momo-callback\`,
  });
  return { reference: res.data.data.flw_ref, status: 'pending' };
}`,
      },
    ],
  },
  {
    id: "P3",
    title: "Creator Payouts",
    color: "#FF8C47",
    accent: "#1a0500",
    icon: "◎",
    queue: "Ship Third",
    summary: "Revenue attribution, earnings dashboard, automated disbursement",
    items: [
      {
        id: "P3-1",
        title: "Revenue Attribution Engine",
        priority: "high",
        description: "Track which creators drove which portion of subscription revenue via watch time.",
        shipCriteria: [
          "Monthly attribution totals sum correctly to platform revenue × creator split %",
          "Earnings dashboard shows per-episode breakdown with watch time stats",
          "Attribution cron runs on 1st of each month without manual trigger",
        ],
        techStack: ["PostgreSQL", "Node.js cron", "React (web portal)"],
        tasks: [
          "Schema: revenue_events(id, creator_id, episode_id, amount_ugx, split_pct, month, created_at)",
          "Define split rules: 70% creator / 30% platform for subscriptions, 60/40 for ad revenue",
          "Monthly cron: total sub revenue / platform total watch-ms * creator watch-ms * split %",
          "Write calculateCreatorEarnings(creatorId, month) — unit test with fixture data",
          "Build GET /creator/earnings: monthly totals, per-episode breakdown, payout history",
          "Build <EarningsDashboard> screen: earnings chart over time + per-episode table",
        ],
        code: `// earningsService.ts
export async function calculateCreatorEarnings(creatorId: string, month: Date) {
  const watchData = await db.query(\`
    SELECT e.id, e.title,
           SUM(wh.watch_duration_ms) as total_ms
    FROM episodes e
    JOIN watch_history wh ON wh.episode_id = e.id
    WHERE e.creator_id = $1
      AND date_trunc('month', wh.created_at) = $2
    GROUP BY e.id
  \`, [creatorId, month]);

  const platformRevenue = await getMonthlySubscriptionRevenue(month);
  const platformTotalMs = await getTotalPlatformWatchMs(month);

  return watchData.map(ep => ({
    episodeId: ep.id,
    title: ep.title,
    watchMs: ep.total_ms,
    earnedUgx: Math.floor(
      (ep.total_ms / platformTotalMs) * platformRevenue * CREATOR_SPLIT_PCT
    ),
  }));
}`,
      },
      {
        id: "P3-2",
        title: "Payout Disbursement",
        priority: "high",
        description: "Monthly automated payouts to creators via Stripe Connect or Mobile Money.",
        shipCriteria: [
          "Creator can add a payout method (bank via Stripe Connect or Mobile Money number)",
          "Monthly cron creates payout records and initiates transfers",
          "Creator receives push notification + PDF earnings statement after disbursement",
          "Amounts below 50,000 UGX roll over to next month automatically",
        ],
        techStack: ["Stripe Connect", "Flutterwave Transfers", "Node.js", "PDFKit"],
        tasks: [
          "Build <PayoutMethodSetup> screen: toggle between Bank Account (Stripe Connect) or Mobile Money",
          "Stripe Connect: initiate Express onboarding, store stripe_account_id on creator record",
          "Mobile Money payout: store verified phone number for disbursements",
          "Monthly cron (1st of month, 09:00 EAT): aggregate earnings, create payout_records, initiate transfers",
          "Stripe Connect: stripe.transfers.create({ amount, currency: 'ugx', destination: creator.stripeAccountId })",
          "MoMo disbursement: Flutterwave /transfers endpoint with UGX amount",
          "On transfer success: push notification + generate PDF statement via PDFKit",
          "Admin dashboard: view all pending/completed payouts, manual override if needed",
        ],
        code: `// payoutService.ts
const MIN_PAYOUT_UGX = 50_000;

export async function runMonthlyPayouts(month: Date) {
  const creators = await db.creators.findAll({ where: { payoutEnabled: true } });

  for (const creator of creators) {
    const earnings = await sumCreatorEarnings(creator.id, month);

    if (earnings.totalUgx < MIN_PAYOUT_UGX) {
      await rolloverEarnings(creator.id, month);
      continue;
    }

    const record = await db.payouts.create({
      creatorId: creator.id, month,
      amountUgx: earnings.totalUgx, status: 'pending',
    });

    try {
      if (creator.payoutMethod === 'stripe') {
        await stripe.transfers.create({
          amount: ugxToStripeAmount(earnings.totalUgx),
          currency: 'ugx',
          destination: creator.stripeAccountId,
        });
      } else {
        await flutterwave.post('/transfers', {
          account_bank: 'MPS',
          account_number: creator.momoPhone,
          amount: earnings.totalUgx,
          currency: 'UGX',
          reference: \`payout-\${record.id}\`,
        });
      }
      await db.payouts.update(record.id, { status: 'completed' });
      await notifyCreatorPayout(creator, earnings);
    } catch (err) {
      await db.payouts.update(record.id, { status: 'failed', error: err.message });
    }
  }
}`,
      },
    ],
  },
  {
    id: "P4",
    title: "Sponsor Workflows",
    color: "#C47AFF",
    accent: "#0d0015",
    icon: "✦",
    queue: "Ship Fourth",
    summary: "Deal management, ad insertion, impression tracking, sponsor reports",
    items: [
      {
        id: "P4-1",
        title: "Sponsor Deal Lifecycle",
        priority: "medium",
        description: "Sponsor request → creator accept/reject → admin approval → ad goes live.",
        shipCriteria: [
          "Sponsor can submit deal with creative assets and flight dates",
          "Creator receives notification and can accept or reject with a note",
          "Admin approval queue works — activated deals go live on target episodes",
          "Creator can define blocked ad categories per channel",
        ],
        techStack: ["Node.js", "AWS S3", "PostgreSQL", "React (web portal)"],
        tasks: [
          "Schema: sponsors, sponsor_deals(id, sponsor_id, creator_id, budget_ugx, start_date, end_date, status), ad_placements(deal_id, episode_id, position_ms, type)",
          "Sponsor Portal (web): deal request form, creative video upload to S3, campaign dashboard",
          "Creator side: view incoming deal requests with budget/brief, accept/reject with optional note",
          "Admin queue: Binyuma team reviews all deals before status → active",
          "Notify each party on status change via push + email",
          "Creator settings: blocked_categories[] stored on creator record, enforced at deal matching",
        ],
        code: `// dealService.ts
export async function submitDealRequest(params: {
  sponsorId: string; creatorId: string; budget: number;
  brief: string; adUrl: string; startDate: Date; endDate: Date;
}) {
  const deal = await db.sponsorDeals.create({ ...params, status: 'pending_creator' });
  await notify(params.creatorId, {
    title: 'New Sponsorship Request',
    body: 'A brand wants to sponsor your content — review the offer.',
    data: { dealId: deal.id },
  });
  return deal;
}

export async function creatorRespondToDeal(dealId: string, accepted: boolean, note?: string) {
  const status = accepted ? 'pending_admin' : 'rejected_by_creator';
  await db.sponsorDeals.update(dealId, { status, creatorNote: note });
  if (accepted) await notifyAdmins({ type: 'deal_pending_review', dealId });
}`,
      },
      {
        id: "P4-2",
        title: "Ad Serving & Impression Tracking",
        priority: "medium",
        description: "Inject pre-roll/mid-roll ads into playback and track completion rates.",
        shipCriteria: [
          "Premium subscribers see no pre-roll ads",
          "Impression logged per unique view with watched_pct field",
          "Sponsor receives weekly report email with impressions, completion rate, reach",
          "Ad serving adds less than 200ms to episode load time",
        ],
        techStack: ["Node.js", "PostgreSQL", "AWS SES", "PDFKit"],
        tasks: [
          "Build getEpisodeAdPlacements(episodeId, userId) — filters by user plan and active deals",
          "Player: before loading episode, fetch placements and queue pre-roll URL if applicable",
          "Mid-roll: pause episode at position_ms, play ad, resume on completion or skip",
          "Log ad_impressions(placement_id, user_id, watched_pct, device, timestamp) on ad end",
          "Weekly cron: aggregate impressions per deal, generate PDF report, email sponsor",
          "Sponsor dashboard (web): live impression counter, completion rate chart, spend tracker",
        ],
        code: `// adServer.ts
export async function getEpisodeAdPlacements(episodeId: string, userId: string) {
  const userPlan = await getUserPlan(userId);

  const placements = await db.query(\`
    SELECT ap.*, s.name as sponsor_name, s.ad_url, s.skip_after_ms
    FROM ad_placements ap
    JOIN sponsor_deals sd ON sd.id = ap.deal_id
    JOIN sponsors s ON s.id = sd.sponsor_id
    WHERE ap.episode_id = $1
      AND sd.status = 'active'
      AND sd.start_date <= NOW() AND sd.end_date >= NOW()
  \`, [episodeId]);

  // Premium users skip pre-roll
  return placements.filter(p =>
    userPlan !== 'premium' || p.type === 'mid_roll'
  );
}

export async function logImpression(placementId: string, userId: string, watchedPct: number) {
  await db.adImpressions.create({
    placementId, userId, watchedPct,
    completedView: watchedPct >= 0.8,
    timestamp: new Date(),
  });
}`,
      },
    ],
  },
];

const priorityBadge = {
  critical: { bg: "#FF4747", text: "#fff", label: "CRITICAL" },
  high: { bg: "#E8FF47", text: "#000", label: "HIGH" },
  medium: { bg: "#2a2a2a", text: "#777", label: "MEDIUM" },
};

function CodeBlock({ code }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ position: "relative", marginTop: 16 }}>
      <button
        onClick={() => { navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
        style={{
          position: "absolute", top: 10, right: 10,
          background: copied ? "#47FFB2" : "#1a1a1a",
          color: copied ? "#000" : "#555",
          border: "1px solid #2a2a2a", borderRadius: 4,
          padding: "3px 10px", fontSize: 11, cursor: "pointer",
          fontFamily: "monospace", transition: "all 0.15s",
        }}
      >
        {copied ? "✓ copied" : "copy"}
      </button>
      <pre style={{
        background: "#060606", border: "1px solid #1a1a1a", borderRadius: 8,
        padding: "16px 20px", overflowX: "auto",
        fontSize: 12, lineHeight: 1.75, color: "#a8b8c8",
        fontFamily: "'Fira Code', monospace", margin: 0,
      }}>
        {code}
      </pre>
    </div>
  );
}

function TaskItem({ task }) {
  const [done, setDone] = useState(false);
  return (
    <div onClick={() => setDone(!done)} style={{
      display: "flex", alignItems: "flex-start", gap: 10,
      padding: "7px 0", cursor: "pointer",
      opacity: done ? 0.4 : 1, transition: "opacity 0.2s",
    }}>
      <div style={{
        width: 16, height: 16, borderRadius: 3, flexShrink: 0, marginTop: 2,
        border: done ? "none" : "1px solid #333",
        background: done ? "#47FFB2" : "transparent",
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "all 0.15s",
      }}>
        {done && <span style={{ fontSize: 10, color: "#000", fontWeight: 700 }}>✓</span>}
      </div>
      <span style={{
        fontSize: 13, color: done ? "#3a3a3a" : "#bbb", lineHeight: 1.55,
        textDecoration: done ? "line-through" : "none",
        fontFamily: "Georgia, serif",
      }}>
        {task}
      </span>
    </div>
  );
}

function ShipCriterion({ text, phaseColor }) {
  const [passed, setPassed] = useState(false);
  return (
    <div onClick={() => setPassed(!passed)} style={{
      display: "flex", alignItems: "flex-start", gap: 10,
      padding: "6px 0", cursor: "pointer",
      opacity: passed ? 0.45 : 1, transition: "opacity 0.2s",
    }}>
      <div style={{
        width: 16, height: 16, borderRadius: "50%", flexShrink: 0, marginTop: 2,
        border: passed ? "none" : `1px solid ${phaseColor}44`,
        background: passed ? phaseColor : "transparent",
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "all 0.15s",
      }}>
        {passed && <span style={{ fontSize: 9, color: "#000", fontWeight: 700 }}>✓</span>}
      </div>
      <span style={{
        fontSize: 12.5, color: passed ? "#3a3a3a" : phaseColor + "cc",
        lineHeight: 1.5, textDecoration: passed ? "line-through" : "none",
        fontFamily: "Georgia, serif", fontStyle: "italic",
        transition: "color 0.2s",
      }}>
        {text}
      </span>
    </div>
  );
}

function FeatureCard({ item, phaseColor }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("tasks");
  const pb = priorityBadge[item.priority];

  return (
    <div style={{
      background: "#0c0c0c",
      border: `1px solid ${open ? phaseColor + "28" : "#1c1c1c"}`,
      borderRadius: 10, marginBottom: 10, overflow: "hidden",
      transition: "border-color 0.2s",
    }}>
      <div onClick={() => setOpen(!open)} style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "14px 18px", cursor: "pointer",
      }}>
        <div style={{ width: 5, height: 5, borderRadius: "50%", background: phaseColor, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 14, fontWeight: 500, color: "#e0e0e0", fontFamily: "'DM Mono', monospace" }}>
              {item.title}
            </span>
            <span style={{
              fontSize: 9.5, fontWeight: 700, letterSpacing: "0.07em",
              padding: "2px 7px", borderRadius: 3, background: pb.bg, color: pb.text,
            }}>
              {pb.label}
            </span>
          </div>
          <p style={{ margin: "3px 0 0", fontSize: 12, color: "#4a4a4a", fontFamily: "Georgia, serif", lineHeight: 1.4 }}>
            {item.description}
          </p>
        </div>
        <span style={{
          fontSize: 15, color: open ? phaseColor : "#333",
          transform: open ? "rotate(180deg)" : "none",
          transition: "all 0.2s", flexShrink: 0,
        }}>⌄</span>
      </div>

      {open && (
        <div style={{ borderTop: "1px solid #161616", padding: "0 18px 18px" }}>
          {/* Tech Stack */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", padding: "12px 0" }}>
            {item.techStack.map(t => (
              <span key={t} style={{
                fontSize: 11, padding: "2px 9px", borderRadius: 100,
                border: "1px solid #242424", color: "#555",
                fontFamily: "'DM Mono', monospace",
              }}>{t}</span>
            ))}
          </div>

          {/* Tabs */}
          <div style={{ display: "flex", borderBottom: "1px solid #1e1e1e", marginBottom: 14 }}>
            {[
              { key: "tasks", label: "Tasks" },
              { key: "ship criteria", label: "Ship Criteria" },
              { key: "code", label: "Code" },
            ].map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} style={{
                background: "transparent", border: "none",
                borderBottom: tab === t.key ? `2px solid ${phaseColor}` : "2px solid transparent",
                color: tab === t.key ? phaseColor : "#3a3a3a",
                padding: "7px 14px", fontSize: 10.5, cursor: "pointer",
                fontFamily: "'DM Mono', monospace", letterSpacing: "0.06em",
                marginBottom: -1, transition: "all 0.15s", textTransform: "uppercase",
              }}>{t.label}</button>
            ))}
          </div>

          {tab === "tasks" && (
            <div style={{ background: "#080808", borderRadius: 7, padding: "10px 14px", border: "1px solid #181818" }}>
              <div style={{ fontSize: 9.5, color: "#2e2e2e", letterSpacing: "0.1em", marginBottom: 6, textTransform: "uppercase", fontFamily: "'DM Mono', monospace" }}>
                Click to check off
              </div>
              {item.tasks.map((t, i) => <TaskItem key={i} task={t} />)}
            </div>
          )}

          {tab === "ship criteria" && (
            <div style={{ background: "#070a08", borderRadius: 7, padding: "10px 14px", border: `1px solid ${phaseColor}18` }}>
              <div style={{ fontSize: 9.5, color: phaseColor + "44", letterSpacing: "0.1em", marginBottom: 6, textTransform: "uppercase", fontFamily: "'DM Mono', monospace" }}>
                All must pass before shipping
              </div>
              {item.shipCriteria.map((c, i) => <ShipCriterion key={i} text={c} phaseColor={phaseColor} />)}
            </div>
          )}

          {tab === "code" && <CodeBlock code={item.code} />}
        </div>
      )}
    </div>
  );
}

export default function BinyumaTVPlan() {
  const [activePhase, setActivePhase] = useState(null);

  const totalFeatures = phases.reduce((s, p) => s + p.items.length, 0);
  const totalTasks = phases.reduce((s, p) => s + p.items.reduce((ss, i) => ss + i.tasks.length, 0), 0);
  const totalCriteria = phases.reduce((s, p) => s + p.items.reduce((ss, i) => ss + i.shipCriteria.length, 0), 0);

  return (
    <div style={{ minHeight: "100vh", background: "#080808", color: "#ddd", fontFamily: "'DM Mono', monospace", paddingBottom: 80 }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Mono:ital,wght@0,300;0,400;0,500;1,400&display=swap');
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 3px; }
        ::-webkit-scrollbar-thumb { background: #222; }
        button:focus { outline: none; }
      `}</style>

      {/* Header */}
      <div style={{
        position: "sticky", top: 0, zIndex: 100,
        background: "rgba(8,8,8,0.92)", backdropFilter: "blur(14px)",
        borderBottom: "1px solid #141414", padding: "22px 36px 18px",
      }}>
        <div style={{ maxWidth: 820, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 20 }}>
            <div>
              <div style={{ fontSize: 9, letterSpacing: "0.22em", color: "#E8FF47", marginBottom: 5, textTransform: "uppercase" }}>
                BinyumaTV — Implementation Plan
              </div>
              <h1 style={{ margin: 0, fontSize: 21, fontWeight: 400, color: "#f0f0f0", letterSpacing: "-0.02em" }}>
                Platform Roadmap
              </h1>
              <p style={{ margin: "4px 0 0", fontSize: 11, color: "#383838", fontStyle: "italic", fontFamily: "Georgia, serif" }}>
                Feature-by-feature · ship when all criteria pass
              </p>
            </div>
            <div style={{ display: "flex", gap: 22, flexShrink: 0, paddingTop: 2 }}>
              {[
                { val: totalFeatures, label: "Features", color: "#E8FF47" },
                { val: totalTasks, label: "Tasks", color: "#47FFB2" },
                { val: totalCriteria, label: "Ship checks", color: "#C47AFF" },
              ].map(s => (
                <div key={s.label} style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 20, fontWeight: 500, color: s.color, lineHeight: 1 }}>{s.val}</div>
                  <div style={{ fontSize: 9, color: "#303030", textTransform: "uppercase", letterSpacing: "0.1em", marginTop: 3 }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Phase pills */}
          <div style={{ display: "flex", gap: 6, marginTop: 16, flexWrap: "wrap" }}>
            <button
              onClick={() => setActivePhase(null)}
              style={{
                background: !activePhase ? "#1c1c1c" : "transparent",
                border: "1px solid #252525", color: !activePhase ? "#888" : "#3a3a3a",
                borderRadius: 100, padding: "4px 12px",
                fontSize: 10, cursor: "pointer", fontFamily: "'DM Mono', monospace",
              }}
            >all</button>
            {phases.map(p => (
              <button
                key={p.id}
                onClick={() => setActivePhase(activePhase === p.id ? null : p.id)}
                style={{
                  background: activePhase === p.id ? p.color : "transparent",
                  border: `1px solid ${activePhase === p.id ? p.color : "#252525"}`,
                  color: activePhase === p.id ? p.accent : "#444",
                  borderRadius: 100, padding: "4px 13px",
                  fontSize: 10, cursor: "pointer", fontFamily: "'DM Mono', monospace",
                  fontWeight: activePhase === p.id ? 600 : 400,
                  transition: "all 0.15s",
                }}
              >
                {p.icon} {p.title}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ maxWidth: 820, margin: "0 auto", padding: "36px 36px 0" }}>
        {phases.filter(p => !activePhase || activePhase === p.id).map(phase => (
          <div key={phase.id} style={{ marginBottom: 50 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 8, background: phase.color,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 15, color: phase.accent, flexShrink: 0,
              }}>
                {phase.icon}
              </div>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <h2 style={{ margin: 0, fontSize: 15, fontWeight: 500, color: phase.color }}>
                    {phase.title}
                  </h2>
                  <span style={{
                    fontSize: 9, color: "#2e2e2e", letterSpacing: "0.12em",
                    padding: "2px 8px", border: "1px solid #1e1e1e", borderRadius: 3,
                    textTransform: "uppercase",
                  }}>
                    {phase.queue}
                  </span>
                </div>
                <p style={{ margin: "2px 0 0", fontSize: 11, color: "#3e3e3e", fontStyle: "italic", fontFamily: "Georgia, serif" }}>
                  {phase.summary}
                </p>
              </div>
            </div>

            {phase.items.map(item => (
              <FeatureCard key={item.id} item={item} phaseColor={phase.color} />
            ))}
          </div>
        ))}

        <div style={{ borderTop: "1px solid #141414", paddingTop: 26, fontSize: 10.5, color: "#252525", lineHeight: 2, fontStyle: "italic", fontFamily: "Georgia, serif" }}>
          Tasks are implementation steps — check off as you go.
          Ship Criteria are acceptance tests — a feature isn't done until all pass.
          Code samples are starting points; adapt SKU names, API keys, and split percentages to your production environment.
        </div>
      </div>
    </div>
  );
}
